import React, { useEffect, useState, useRef } from 'react';
import { createRoot } from 'react-dom/client';

/**
 * Custom hook to extract meta tags from a nested component
 * next/head can only extract up to one level deep. These tags are delivered within
 * levels of Suspense, lazy, and error boundaries
 */
function useExtractedMeta(resolvedMeta: React.ElementType | null | undefined, skipExtraction = false): JSX.Element[] {
  const [extractedMeta, setExtractedMeta] = useState<JSX.Element[]>([]);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const rootRef = useRef<ReturnType<typeof createRoot> | null>(null);

  useEffect(() => {
    if (skipExtraction) {
      return;
    }

    if (typeof window === 'undefined') {
      return;
    }

    if (!resolvedMeta) {
      return;
    }

    let isCancelled = false;

    // Create hidden container
    const container = document.createElement('div');
    container.style.position = 'absolute';
    container.style.left = '-9999px';
    container.style.visibility = 'hidden';
    document.body.appendChild(container);
    containerRef.current = container;

    // Create root and render the component using React 18 API
    const root = createRoot(container);
    rootRef.current = root;

    // Use React.createElement with proper typing and cast to avoid React 18 type conflicts
    const element = React.createElement(resolvedMeta as React.ComponentType, {});
    (root.render as (element: any) => void)(element);

    // Use requestAnimationFrame to ensure render is complete, then extract meta elements
    requestAnimationFrame(() => {
      if (isCancelled) {
        return;
      }

      // Additional setTimeout to ensure all effects have run
      setTimeout(() => {
        if (isCancelled) {
          return;
        }

        const titleElements = container.querySelectorAll('title');
        const metaElements = container.querySelectorAll('meta');
        const linkElements = container.querySelectorAll('link[rel]');
        const scriptElements = container.querySelectorAll('script');

        const extracted: JSX.Element[] = [];

        titleElements.forEach((el) => {
          const titleText = el.textContent ?? '';
          extracted.push(<title key={`title-${titleText}`}>{titleText}</title>);
        });

        metaElements.forEach((el) => {
          const props: Record<string, string> = {};
          Array.from(el.attributes).forEach((attr) => {
            props[attr.name] = attr.value;
          });
          const keyValue = props.name || props.property || props.content || 'meta';
          extracted.push(<meta key={`meta-${keyValue}`} {...props} />);
        });

        linkElements.forEach((el) => {
          const props: Record<string, string> = {};
          Array.from(el.attributes).forEach((attr) => {
            props[attr.name] = attr.value;
          });
          const keyValue = `${props.rel || 'link'}-${props.href || 'default'}`;
          extracted.push(<link key={`link-${keyValue}`} {...props} />);
        });

        scriptElements.forEach((el) => {
          const props: Record<string, string> = {};
          Array.from(el.attributes).forEach((attr) => {
            props[attr.name] = attr.value;
          });
          const keyValue = `${props.rel || 'script'}-${props.src || 'default'}`;
          extracted.push(
            <script key={`script-${keyValue}`} {...props}>
              {el.textContent}
            </script>
          );
        });

        setExtractedMeta(extracted);

        // Cleanup using React 18 API - use flushSync to ensure proper timing
        setTimeout(() => {
          if (!isCancelled && rootRef.current) {
            rootRef.current.unmount();
            rootRef.current = null;
          }
          if (!isCancelled && container.parentNode) {
            document.body.removeChild(container);
          }
        }, 0);
      }, 0);
    });

    // Cleanup function for useEffect - use function declaration to avoid linter issues
    function cleanup(): void {
      isCancelled = true;

      // Use setTimeout to avoid synchronous unmounting during render
      setTimeout(() => {
        if (rootRef.current) {
          rootRef.current.unmount();
          rootRef.current = null;
        }
        if (containerRef.current?.parentNode) {
          document.body.removeChild(containerRef.current);
          containerRef.current = null;
        }
      }, 0);
    }

    // eslint-disable-next-line
    return cleanup;
  }, [resolvedMeta, extractedMeta.length, skipExtraction]);

  return extractedMeta;
}

export default useExtractedMeta;
